import { useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { useLanguage } from '../../contexts/language';
import { anything, useField } from '../../hooks/field';
import { useRecordChanges } from '../../hooks/record-changes';
import { useVisit } from '../../hooks/visit';

import { ChoiceList } from '../choice-list';
import { StepActions } from '../step-actions';

import styles from './style.module.css';


// Steps 2 and 3: anxiety or irritability on the sheet's own scale, 0 none,
// 1 mild, 2 moderate, 3 severe. field names which one; the steps are
// otherwise identical, which is why they are one component.
export function LevelStep({ ref, field, question, record, onBack, onSkip, onMove, onSaving, onStopped, onDone }) {
  const { text, translate } = useLanguage();
  const { save } = useRecordChanges();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);

  let initial = null;

  if (record[ field ] != null) {
    initial = String(record[ field ]);
  }

  const level = useField({ initial, check: anything });

  const options = useMemo(function() {
    return [ 0, 1, 2, 3 ].map(function(value) {
      return { key: String(value), label: `${ value } · ${ text.levels[ value ] }` };
    });
  }, [ text ]);

  // Saves what was chosen, then goes to `to`, or to the next step when to is
  // null, as Next does; the bar calls it with the step it chose (#158).
  const submit = useCallback(function(to) {
    if (level.value === '') {
      return onMove(to);
    }

    setBusy(true);

    const move = onSaving(to);
    const owns = capture();

    return save({ date: record.date, input: { [ field ]: Number(level.value) } }).then(function() {
      if (owns() === false) {
        return onStopped(move);
      }

      return onDone(move);
    }).catch(function(failed) {
      onStopped(move);

      if (owns() === false) {
        return;
      }

      setBusy(false);
      level.refuse(failed.message);
    });
  }, [ field, level, record, save, onMove, onSaving, onStopped, onDone, capture ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();
    submit(null);
  }, [ submit ]);

  useImperativeHandle(ref, function() {
    return { submit };
  }, [ submit ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <ChoiceList
        ref={ level.ref }
        name={ field }
        legend={ question }
        options={ options }
        value={ level.value }
        onChange={ level.onChange }
        error={ translate(level.error) }
      />

      <StepActions onBack={ onBack } submitLabel={ text.next } busy={ busy } skipLabel={ text.skip } onSkip={ onSkip }/>
    </form>
  );
}
